import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiCall } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { ToastContext } from '../App';

interface Animal {
  id: number;
  name: string;
  birth_date: string;
  observations?: string;
}

interface StudentAnimal extends Animal {
  user_id: number;
  user_name: string;
  student_category: string;
  role: string;
}

interface StudentWithAnimals {
  user_id: number;
  user_name: string;
  student_category: string;
  role: string;
  animals: StudentAnimal[];
}

const AnimalsPage: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = React.useContext(ToastContext);
  const isTrainer = user?.role === 'trainer';
  
  // Estados para estudantes
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ name: '', birth_date: '', observations: '' });
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // Estados para treinadores
  const [studentAnimals, setStudentAnimals] = useState<StudentWithAnimals[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Estados para modais de confirmação
  const [animalToDelete, setAnimalToDelete] = useState<number | null>(null);

  // Estados para formulário de novo animal
  const [showNewAnimalForm, setShowNewAnimalForm] = useState(false);
  const [newAnimal, setNewAnimal] = useState({ name: '', birth_date: '', observations: '' });
  const [newAnimalLoading, setNewAnimalLoading] = useState(false);
  const [newAnimalError, setNewAnimalError] = useState('');

  // Handler para abrir/fechar formulário
  const toggleNewAnimalForm = () => {
    setShowNewAnimalForm((prev) => !prev);
    setNewAnimal({ name: '', birth_date: '', observations: '' });
    setNewAnimalError('');
  };

  // Handler para inputs do novo animal
  const handleNewAnimalChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewAnimal({ ...newAnimal, [e.target.name]: e.target.value });
  };

  // Submeter novo animal
  const handleCreateAnimal = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewAnimalError('');
    if (!newAnimal.name || !newAnimal.birth_date) {
      setNewAnimalError('Nome e data de nascimento são obrigatórios.');
      showToast('Nome e data de nascimento são obrigatórios.', 'error');
      return;
    }
    setNewAnimalLoading(true);
    try {
      const data = await apiCall('/animals', {
        method: 'POST',
        body: JSON.stringify(newAnimal)
      });
      if (data.animal) {
        await fetchAnimals();
        setShowNewAnimalForm(false);
        setNewAnimal({ name: '', birth_date: '', observations: '' });
        showToast('Animal criado com sucesso!', 'success');
      }
    } catch (err: any) {
      setNewAnimalError(err.message || 'Erro ao criar animal');
      showToast(err.message || 'Erro ao criar animal', 'error');
    } finally {
      setNewAnimalLoading(false);
    }
  };

  useEffect(() => {
    if (isTrainer) {
      fetchStudentAnimals();
    } else {
      fetchAnimals();
    }
  }, [isTrainer]);

  const fetchAnimals = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiCall('/animals/my');
      console.log('Animals data received:', data);
      const animalsData = data.animals || [];
      console.log('Processed animals:', animalsData);
      setAnimals(animalsData);
    } catch (err) {
      setError('Erro ao comunicar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentAnimals = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiCall('/animals/with-students');
      console.log('Student animals data received:', data);
      const animalsData = data.animals || [];
      
      // Agrupar animais por aluno
      const animalsByStudent: { [key: number]: StudentWithAnimals } = {};
      animalsData.forEach((animal: StudentAnimal) => {
        if (!animalsByStudent[animal.user_id]) {
          animalsByStudent[animal.user_id] = {
            user_id: animal.user_id,
            user_name: animal.user_name,
            student_category: animal.student_category,
            role: animal.role,
            animals: []
          };
        }
        animalsByStudent[animal.user_id].animals.push(animal);
      });
      
      setStudentAnimals(Object.values(animalsByStudent));
    } catch (err) {
      setError('Erro ao comunicar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (animal: Animal) => {
    console.log('Opening edit for animal:', animal);
    if (!animal.id) {
      console.error('Animal ID is missing:', animal);
      setEditError('Erro: ID do animal não encontrado');
      return;
    }
    setEditId(animal.id);
    setEditForm({ name: animal.name, birth_date: animal.birth_date, observations: animal.observations || '' });
    setEditError('');
  };

  const closeEdit = () => {
    setEditId(null);
    setEditForm({ name: '', birth_date: '', observations: '' });
    setEditError('');
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleEditAnimal = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError('');
    if (!editForm.name || !editForm.birth_date) {
      setEditError('Nome e data de nascimento são obrigatórios.');
      showToast('Nome e data de nascimento são obrigatórios.', 'error');
      return;
    }
    if (!editId) {
      setEditError('Erro: ID do animal não encontrado');
      showToast('Erro: ID do animal não encontrado', 'error');
      return;
    }
    console.log('Submitting edit for animal ID:', editId);
    setEditLoading(true);
    try {
      const endpoint = user?.role === 'trainer' ? `/animals/trainer/${editId}` : `/animals/${editId}`;
      const data = await apiCall(endpoint, {
        method: 'PUT',
        body: JSON.stringify(editForm)
      });
      if (data.animal) {
        if (user?.role === 'trainer') {
          await fetchStudentAnimals();
        } else {
          await fetchAnimals();
        }
        closeEdit();
        showToast('Animal editado com sucesso!', 'success');
      }
    } catch (err: any) {
      setEditError(err.message || 'Erro ao editar animal');
      showToast(err.message || 'Erro ao editar animal', 'error');
    } finally {
      setEditLoading(false);
    }
  };

  const showDeleteConfirmation = (id: number) => {
    setAnimalToDelete(id);
  };

  const handleDeleteAnimal = async () => {
    if (!animalToDelete) return;
    
    try {
      await apiCall(`/animals/${animalToDelete}`, {
        method: 'DELETE'
      });
      setAnimals(prev => prev.filter(a => a.id !== animalToDelete));
      setAnimalToDelete(null);
      showToast('Animal eliminado com sucesso!', 'success');
    } catch {
      showToast('Erro ao eliminar animal', 'error');
    }
  };

  const calculateAge = (birthDate: string) => {
    const birth = new Date(birthDate);
    const today = new Date();
    const ageInYears = today.getFullYear() - birth.getFullYear();
    const ageInMonths = today.getMonth() - birth.getMonth();
    
    let ageText = '';
    if (ageInYears > 0) {
      ageText += `${ageInYears} ano${ageInYears > 1 ? 's' : ''}`;
      if (ageInMonths > 0) {
        ageText += ` e ${ageInMonths} ${ageInMonths === 1 ? 'mês' : 'meses'}`;
      }
    } else {
      ageText = `${ageInMonths} ${ageInMonths === 1 ? 'mês' : 'meses'}`;
    }
    return ageText;
  };

  const getCategoryBadge = (student: StudentWithAnimals) => {
    if (student.role === 'guest') {
      return <span className="badge bg-warning ms-2">Convidado</span>;
    } else if (student.student_category === 'external') {
      return <span className="badge bg-info ms-2">Externo</span>;
    } else {
      return <span className="badge bg-success ms-2">Aluno</span>;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'student':
        return 'fas fa-user-graduate';
      case 'external':
        return 'fas fa-user-tie';
      case 'guest':
        return 'fas fa-user-clock';
      default:
        return 'fas fa-user';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'student':
        return 'text-success';
      case 'external':
        return 'text-info';
      case 'guest':
        return 'text-warning';
      default:
        return 'text-primary';
    }
  };

  const filteredStudents = studentAnimals.filter(student => {
    // Filtrar por categoria
    if (selectedCategory !== 'all') {
      if (selectedCategory === 'guest' && student.role !== 'guest') return false;
      if (selectedCategory === 'external' && student.student_category !== 'external') return false;
      if (selectedCategory === 'student' && (student.role === 'guest' || student.student_category === 'external')) return false;
    }
    
    // Filtrar por pesquisa
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const studentNameMatch = student.user_name.toLowerCase().includes(searchLower);
      const animalNameMatch = student.animals.some(animal => 
        animal.name.toLowerCase().includes(searchLower)
      );
      return studentNameMatch || animalNameMatch;
    }
    
    return true;
  });

  // Ordenar alfabeticamente pelo nome do aluno
  const sortedStudents = [...filteredStudents].sort((a, b) => a.user_name.localeCompare(b.user_name, 'pt', { sensitivity: 'base' }));

  const groupedStudents = {
    student: sortedStudents.filter(s => s.role !== 'guest' && s.student_category !== 'external'),
    external: sortedStudents.filter(s => s.student_category === 'external'),
    guest: sortedStudents.filter(s => s.role === 'guest')
  };

  return (
    <div className="container py-4">
      {isTrainer ? (
        <div className="card shadow">
          <div className="card-header mb-3" style={{backgroundColor: '#a2449c', color: 'white'}}>
            <h5 className="mb-0"><i className="fas fa-paw me-2"></i>Gestão de Animais</h5>
          </div>
          <div className="card-body">
            {/* Barra de pesquisa e filtros */}
            <div className="row gy-3 mb-4 align-items-end" style={{marginTop: '0.5rem'}}>
              <div className="col-12 col-md-6">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Pesquisar por aluno ou animal..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setSearchTerm('')}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="btn-group w-100" role="group">
                  <button
                    type="button"
                    className={`btn btn-outline-secondary ${selectedCategory === 'all' ? 'active' : ''}`}
                    style={{ borderColor: '#a2449c', color: selectedCategory === 'all' ? 'white' : '#a2449c', backgroundColor: selectedCategory === 'all' ? '#a2449c' : 'white' }}
                    onClick={() => setSelectedCategory('all')}
                  >
                    Todos
                  </button>
                  <button
                    type="button"
                    className={`btn btn-outline-secondary ${selectedCategory === 'student' ? 'active' : ''}`}
                    style={{ borderColor: '#7c3fa0', color: selectedCategory === 'student' ? 'white' : '#7c3fa0', backgroundColor: selectedCategory === 'student' ? '#7c3fa0' : 'white' }}
                    onClick={() => setSelectedCategory('student')}
                  >
                    Alunos
                  </button>
                  <button
                    type="button"
                    className={`btn btn-outline-secondary ${selectedCategory === 'external' ? 'active' : ''}`}
                    style={{ borderColor: '#b266b2', color: selectedCategory === 'external' ? 'white' : '#b266b2', backgroundColor: selectedCategory === 'external' ? '#b266b2' : 'white' }}
                    onClick={() => setSelectedCategory('external')}
                  >
                    Externos
                  </button>
                </div>
              </div>
            </div>
            {loading && <LoadingSpinner />}
            {error && <div className="alert alert-danger">{error}</div>}
            {!loading && !error && (
              <div id="animalsManagementList">
                {filteredStudents.length === 0 && (
                  <div className="text-center text-muted">
                    <i className="fas fa-search fa-3x mb-3"></i>
                    <p>Nenhum animal encontrado.</p>
                  </div>
                )}
                {/* Alunos Regulares */}
                {groupedStudents.student.length > 0 && (
                  <div className="mb-4">
                    <h6 className={`mb-3 ${getCategoryColor('student')}`}> <i className={`${getCategoryIcon('student')} me-2`}></i> Alunos Regulares ({groupedStudents.student.length}) </h6>
                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 gy-3 gx-3">
                    {groupedStudents.student.map(student => (
                      <div key={student.user_id} className="col">
                        <div className="card h-100">
                          <div className="card-header">
                            <h6 className="mb-0">
                              {student.user_name}
                              {getCategoryBadge(student)}
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="row gy-2 mt-2">
                              {student.animals.map(animal => (
                                <div key={animal.id} className="col-12">
                                  <div className="animal-item border rounded p-3 h-100 position-relative">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <h6 className="fw-bold mb-0" style={{ color: '#a2449c' }}>{animal.name}</h6>
                                      <button
                                        className="btn btn-sm btn-outline-primary position-absolute top-0 end-0 m-2"
                                        title="Editar animal"
                                        onClick={() => openEdit(animal)}
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                    </div>
                                    <p className="text-muted small mb-1">
                                      <strong>Nascimento:</strong> {animal.birth_date}
                                    </p>
                                    <p className="text-muted small mb-1">
                                      <strong>Idade:</strong> {calculateAge(animal.birth_date)}
                                    </p>
                                    {animal.observations && (
                                      <p className="text-muted small mb-0">
                                        <strong>Obs:</strong> {animal.observations}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    </div>
                  </div>
                )}

                {/* Alunos Externos */}
                {groupedStudents.external.length > 0 && (
                  <div className="mb-4">
                    <h6 className={`mb-3 ${getCategoryColor('external')}`}>
                      <i className={`${getCategoryIcon('external')} me-2`}></i>
                      Alunos Externos ({groupedStudents.external.length})
                    </h6>
                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 gy-3 gx-3">
                    {groupedStudents.external.map(student => (
                      <div key={student.user_id} className="col">
                        <div className="card h-100">
                          <div className="card-header">
                            <h6 className="mb-0">
                              {student.user_name}
                              {getCategoryBadge(student)}
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="row gy-2 mt-2">
                              {student.animals.map(animal => (
                                <div key={animal.id} className="col-12">
                                  <div className="animal-item border rounded p-3 h-100 position-relative">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <h6 className="fw-bold mb-0" style={{ color: '#a2449c' }}>{animal.name}</h6>
                                      <button
                                        className="btn btn-sm btn-outline-primary position-absolute top-0 end-0 m-2"
                                        title="Editar animal"
                                        onClick={() => openEdit(animal)}
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                    </div>
                                    <p className="text-muted small mb-1">
                                      <strong>Nascimento:</strong> {animal.birth_date}
                                    </p>
                                    <p className="text-muted small mb-1">
                                      <strong>Idade:</strong> {calculateAge(animal.birth_date)}
                                    </p>
                                    {animal.observations && (
                                      <p className="text-muted small mb-0">
                                        <strong>Obs:</strong> {animal.observations}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    </div>
                  </div>
                )}

                {/* Convidados */}
                {groupedStudents.guest.length > 0 && (
                  <div className="mb-4">
                    <h6 className={`mb-3 ${getCategoryColor('guest')}`}>
                      <i className={`${getCategoryIcon('guest')} me-2`}></i>
                      Convidados ({groupedStudents.guest.length})
                    </h6>
                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 gy-3 gx-3">
                    {groupedStudents.guest.map(student => (
                      <div key={student.user_id} className="col">
                        <div className="card h-100">
                          <div className="card-header">
                            <h6 className="mb-0">
                              {student.user_name}
                              {getCategoryBadge(student)}
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="row gy-2 mt-2">
                              {student.animals.map(animal => (
                                <div key={animal.id} className="col-12">
                                  <div className="animal-item border rounded p-3 h-100 position-relative">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <h6 className="fw-bold mb-0" style={{ color: '#a2449c' }}>{animal.name}</h6>
                                      <button
                                        className="btn btn-sm btn-outline-primary position-absolute top-0 end-0 m-2"
                                        title="Editar animal"
                                        onClick={() => openEdit(animal)}
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                    </div>
                                    <p className="text-muted small mb-1">
                                      <strong>Nascimento:</strong> {animal.birth_date}
                                    </p>
                                    <p className="text-muted small mb-1">
                                      <strong>Idade:</strong> {calculateAge(animal.birth_date)}
                                    </p>
                                    {animal.observations && (
                                      <p className="text-muted small mb-0">
                                        <strong>Obs:</strong> {animal.observations}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="card shadow">
          <div className="card-header mb-3" style={{backgroundColor: '#a2449c', color: 'white'}}>
            <h5 className="mb-0"><i className="fas fa-paw me-2"></i>Gestão de Animais</h5>
          </div>
          <div className="card-body">
            {loading && <div className="text-center py-4"><span className="spinner-border"></span></div>}
            {error && <div className="alert alert-danger">{error}</div>}
            {!loading && !error && (
              <div id="animalsList">
                {!isTrainer && (
                  <div className="mb-4">
                    <div className="card mb-3 shadow-sm">
                      <div className="card-body">
                        <form onSubmit={handleCreateAnimal}>
                          <div className="row g-2 align-items-end">
                            <div className="col-12 col-md">
                              <label className="form-label mb-1">Nome</label>
                              <input type="text" className="form-control" name="name" value={newAnimal.name} onChange={handleNewAnimalChange} required />
                            </div>
                            <div className="col-12 col-md">
                              <label className="form-label mb-1">Data de Nascimento</label>
                              <input type="date" className="form-control" name="birth_date" value={newAnimal.birth_date} onChange={handleNewAnimalChange} required />
                            </div>
                            <div className="col-12 col-md">
                              <label className="form-label mb-1">Observações</label>
                              <input type="text" className="form-control" name="observations" value={newAnimal.observations} onChange={handleNewAnimalChange} />
                            </div>
                            <div className="col-12 col-md-auto">
                              <button type="submit" className="btn btn-primary w-100" style={{ backgroundColor: '#a2449c', border: 'none' }} disabled={newAnimalLoading}>
                                {newAnimalLoading ? 'A criar...' : 'Criar'}
                              </button>
                            </div>
                          </div>
                          {newAnimalError && <div className="alert alert-danger py-2 mt-2">{newAnimalError}</div>}
                        </form>
                      </div>
                    </div>
                  </div>
                )}
                <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 gy-3 gx-3">
                  {animals.length === 0 && (
                    <div className="col text-center text-muted">
                      <i className="fas fa-search fa-3x mb-3"></i>
                      <p>Nenhum animal encontrado.</p>
                    </div>
                  )}
                  {animals.map(animal => (
                    <div key={animal.id} className="col">
                      <div className="card h-100">
                        <div className="card-body">
                          <h6 className="fw-bold mb-1 fs-5" style={{ color: '#a2449c', fontSize: '1.25rem' }}>{animal.name}</h6>
                          <div className="mb-1"><strong>Nascimento:</strong> {animal.birth_date}</div>
                          <div className="mb-1"><strong>Idade:</strong> {calculateAge(animal.birth_date)}</div>
                          {animal.observations && <div className="mb-1"><strong>Obs:</strong> {animal.observations}</div>}
                          <div className="d-flex justify-content-end gap-2 mt-2">
                            <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(animal)}><i className="fas fa-edit"></i></button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => showDeleteConfirmation(animal.id)}><i className="fas fa-trash"></i></button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Modal de edição de animal */}
      {editId !== null && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.3)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleEditAnimal}>
                <div className="modal-header">
                  <h5 className="modal-title">Editar Animal</h5>
                  <button type="button" className="btn-close" onClick={closeEdit}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Nome</label>
                    <input type="text" className="form-control" name="name" value={editForm.name} onChange={handleEditChange} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Data de Nascimento</label>
                    <input type="date" className="form-control" name="birth_date" value={editForm.birth_date} onChange={handleEditChange} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Observações</label>
                    <textarea className="form-control" name="observations" value={editForm.observations} onChange={handleEditChange} rows={2} />
                  </div>
                  {editError && <div className="alert alert-danger py-2">{editError}</div>}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeEdit} disabled={editLoading}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={editLoading}>
                    {editLoading ? 'A guardar...' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação de deleção */}
      {animalToDelete !== null && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.3)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmar Eliminação</h5>
                <button type="button" className="btn-close" onClick={() => setAnimalToDelete(null)}></button>
              </div>
              <div className="modal-body">
                <p>Tens a certeza que queres eliminar este animal? Esta ação não pode ser desfeita.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setAnimalToDelete(null)}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-danger" onClick={handleDeleteAnimal}>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnimalsPage; 